import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { AiProfileInfo } from '@/customTypes/chapter';
import {
  StoryGameArrowRight,
  StoryGameArrowLeft,
  StoryGameProfileEdit,
} from 'public/assets';

interface StoryGamePreviewCharacterNameProps {
  onPrevious?: () => void;
  onNext?: () => void;
  firstName: string;
  onChangeAiProfile: (profile: AiProfileInfo) => void;
  lastName: string;
  characterImage?: string | File;
  onProfileEdit: () => void;
  displayName: string;
  isDefault: boolean;
}

const StoryGamePreviewCharacterName: React.FC<
  StoryGamePreviewCharacterNameProps
> = ({
  onPrevious,
  onNext,
  firstName,
  lastName,
  onProfileEdit,
  isDefault,
  displayName,
}) => {
  const textRef = useRef<HTMLHeadingElement>(null);
  const [imageMargin, setImageMargin] = useState(0);
  useEffect(() => {
    const updateImagePosition = () => {
      if (textRef.current) {
        const textWidth = textRef.current.offsetWidth;
        const newMargin = textWidth / 2 + 10; // 10px은 추가 간격
        setImageMargin(newMargin);
      }
    };

    updateImagePosition();
    // 텍스트 내용이 변경될 때마다 위치 재계산
    window.addEventListener('resize', updateImagePosition);
    return () => window.removeEventListener('resize', updateImagePosition);
  }, [lastName, firstName]);
  return (
    <div css={styles.container}>
      <button css={styles.navButton} onClick={onPrevious}>
        <StoryGameArrowLeft color="white" />
      </button>
      <div css={styles.innerContainer}>
        <div css={styles.contentContainer}>
          <div css={styles.textContainer}>
            <h2 ref={textRef} css={styles.title}>
              {lastName + '' + firstName}
              <StoryGameProfileEdit
                onClick={onProfileEdit}
                style={{
                  position: 'absolute',
                  left: '50%',
                  marginLeft: `${imageMargin}px`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                }}
              />
            </h2>
          </div>
          <div css={styles.textContainer}>
            <h2 css={styles.subTitle}>{displayName}</h2>
          </div>
        </div>
      </div>
      <button css={styles.navButton} onClick={onNext}>
        <StoryGameArrowRight color={'white'} />
      </button>
    </div>
  );
};

const styles = {
  container: css`
    position: relative;
    min-width: 512px;
    width: 32vw;
    border-top: 2px solid;
    border-bottom: 2px solid;
    border-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    border-image-slice: 1;

    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.75) 20%,
      rgba(0, 0, 0, 0.75) 80%,
      rgba(0, 0, 0, 0) 100%
    );
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 36px 40px;
  `,
  innerContainer: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
  `,
  imageContainer: css`
    position: absolute;
    top: -55px;
    width: 80px;
    height: 80px;
    border-radius: 40px;
    border: 2px solid rgba(255, 255, 255, 0.5);
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 40px;
  `,
  contentContainer: css`
    width: 100%;
    padding: 0px 20px;
  `,
  textContainer: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
  `,

  title: css`
    margin-bottom: 10px;
    position: relative;
    display: inline-block;

    color: #ffffffe5;
    text-align: center;
    text-overflow: ellipsis;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
  `,
  subTitle: css`
    margin-bottom: 10px;
    position: relative;
    display: inline-block;

    color: #ffffffe5;
    text-align: center;
    text-overflow: ellipsis;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
  `,
  inputContainer: css`
    display: flex;
    justify-content: space-between;
    gap: 10px;
  `,
  input: css`
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 5px;
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 16px;
    text-align: center;
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  `,
  navButton: css`
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 10px;
    z-index: 1;
    &:hover {
      opacity: 0.8;
    }
  `,
  profileEditIcn: css`
    position: absolute;
    display: flex;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
    bottom: 0px;
    right: 0px;
    border-radius: 100px;
    border: 1px solid var(--blackA25, rgba(0, 0, 0, 0.25));
    background: #fff;
    cursor: pointer;
  `,
  emptyImageContainer: css`
    position: absolute;
    top: -55px;
    width: 80px;
    height: 80px;
    border: 2px solid var(--whiteA50, rgba(255, 255, 255, 0.5));
    border-radius: 40px;
    background: #ddd;
    box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.5) inset;
    border-radius: 40px;
  `,
  emptyImage: css`
    width: 100%;
    height: 100%;
  `,
};

export default StoryGamePreviewCharacterName;
